import { useEffect, useRef, useState } from "react";
import "./index.scss";

import {
  Button,
  Checkbox,
  Form,
  Input,
  Space,
  Row,
  Col,
  message,
  Modal,
} from "antd";
import { verifyCode, getVerifyCaptcha } from "../../api/user";
import { useTranslation } from "react-i18next";
import { isTell } from "../../utils/tool";
import GoCaptcha from "go-captcha-react";

type Props = {
  form: any;
  showLable?: boolean;
  styles?: { [key: string]: string };
};

type FieldType = {
  phone: string;
  code: string;
};

const default_count = 60;
const UserInput = ({ form, styles, showLable = true }: Props) => {
  const { t, i18n } = useTranslation();
  const timer = useRef(null);
  // 倒计时秒数
  const [count, setCount] = useState(default_count);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  // 人机验证相关状态
  const [verifyModalVisible, setVerifyModalVisible] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [captchaData, setCaptchaData] = useState<{
    key: string;
    master: string;
    thumb: string;
    type: string;
  } | null>(null);
  const captchaRef = useRef<any>(null);

  /**
   * 获取验证码图片
   */
  const fetchCaptcha = async () => {
    try {
      const res = await getVerifyCaptcha<{
        key: string;
        master: string;
        thumb: string;
        type: string;
      }>();
      if (res.code === 0 && res.data) {
        setCaptchaData(res.data);
      } else {
        messageApi.error(t("获取验证码失败，请重试"));
      }
    } catch (error) {
      messageApi.error(t("获取验证码失败，请重试"));
    }
  };

  /**
   * 获取验证码
   */
  const getCode = async () => {
    try {
      const values = await form.validateFields(["phone"]);
      if (loading) return;

      // 先显示人机验证
      setVerifyModalVisible(true);
      setIsVerified(false);
      await fetchCaptcha();
    } catch (error) {}
  };

  /**
   * 人机验证确认回调
   */
  const handleVerifyConfirm = async (
    dots: Array<{ key: number; index: number; x: number; y: number }>,
    reset: () => void
  ) => {
    if (!captchaData) {
      reset();
      return false;
    }

    try {
      setIsVerified(true);
      setVerifyModalVisible(false);

      const values = await form.validateFields(["phone"]);

      // 将点击坐标转换为接口需要的格式
      const captcha_dots = dots.map((dot) => ({
        x: dot.x,
        y: dot.y,
      }));

      const res = await verifyCode<{
        status: number;
        internal: number;
      }>({
        mobile: values.phone,
        captcha_dots: captcha_dots,
        captcha_key: captchaData.key,
      });
      if (res.code === 0) {
        setLoading(true);
        timer.current = setInterval(() => {
          setCount((count) => --count);
        }, 1000);
        if (res.data.status === 2) {
          messageApi.error(t("验证码已发送"));
          setCount(0);
          return true;
        }
        return true;
      }
      messageApi.error(res.msg);
      return false;
    } catch (error) {
      reset();
        messageApi.error(t("验证失败，请重试"));
      return false;
    }
  };

  /**
   * 人机验证刷新回调
   */
  const handleVerifyRefresh = async () => {
    setIsVerified(false);
    await fetchCaptcha();
    if (captchaRef.current) {
      captchaRef.current.reset();
    }
  };

  /**
   * 人机验证关闭回调
   */
  const handleVerifyClose = () => {
    setVerifyModalVisible(false);
    setIsVerified(false);
    setCaptchaData(null);
  };

  useEffect(() => {
    if (count <= 0) {
      setCount(default_count);
      clearInterval(timer.current);
      setLoading(false);
    }
  }, [count]);

  // 监听语言变化，重新渲染验证组件
  useEffect(() => {
    if (verifyModalVisible && !captchaData) {
      fetchCaptcha();
    }
  }, [i18n.language, verifyModalVisible]);
  return (
    <>
      {contextHolder}
      <Form
        name="normal_login"
        className="user-form"
        initialValues={{ remember: true }}
        form={form}
        // onFinish={onFinish}
      >
        <Form.Item
          className="input-item"
          name="phone"
          label={showLable && t("手机号")}
          rules={[
            { required: true, message: t("请输入手机号") },
            {
              pattern: new RegExp(/^[1|8]\d{10}$/i),
              message: t("输入的手机号不正确"),
            },
          ]}
        >
          <Input placeholder={t("请输入手机号")} className="phone-input" />
        </Form.Item>
        <Form.Item>
          <Row gutter={8}>
            <Col span={17}>
              <Form.Item
                name="code"
                label={showLable && t("验证码")}
                rules={[
                  { required: true, message: t("请输入验证码") },
                  {
                    pattern: new RegExp(/^\d{6}$/i),
                    message: t("输入的验证码错误"),
                  },
                ]}
                className="code-input"
              >
                <Input type="text" placeholder={t("请输入验证码")} />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Button
                className="code-button"
                onClick={getCode}
                disabled={loading}
              >
                {loading ? `(${t("已发送")})${count}s` : t("获取验证码")}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
      <Modal
        title={t("人机验证")}
        open={verifyModalVisible}
        onCancel={handleVerifyClose}
        footer={null}
        width={400}
        centered
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "20px 0",
          }}
        >
          {captchaData && (
            <GoCaptcha.Click
              width={400}
              height={120}
              key={`${i18n.language}-${captchaData.key}`}
              ref={captchaRef}
              data={{
                image: captchaData.master,
                thumb: captchaData.thumb,
              }}
              events={{
                confirm: handleVerifyConfirm,
                refresh: handleVerifyRefresh,
                close: handleVerifyClose,
              }}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default UserInput;
